/**
 * Mappers for reconciling UI types with Database types
 *
 * These functions transform between:
 * - QASubmissionRow (database schema - snake_case)
 * - QASubmission    (UI schema - camelCase)
 */

import type { QASubmission, QASubmissionRow, QAChecklist } from "@/services/qa.service";

const DEFAULT_CHECKLIST: QAChecklist = {
  drainCorrect: false,
  noLeaks: false,
  glassAligned: false,
  thresholdPitched: false,
  areaCleaned: false,
  protectionRemoved: false,
};

// ── DB → UI ───────────────────────────────────────────────────────────────

export function toUiQASubmission(db: QASubmissionRow): QASubmission {
  return {
    id: db.id,
    submissionId: db.submission_id ?? db.id,
    jobId: db.job_number ?? db.job_id,
    installerName: db.inspector_name,
    arrivalTime: db.arrival_time ?? "",
    customerPresent: db.customer_present ?? null,
    checklist: db.checklist_items ?? { ...DEFAULT_CHECKLIST },
    photos: db.photos ?? {},
    issueEncountered: db.issue_encountered ?? null,
    issueNotes: db.issue_notes ?? "",
    jobComplete: db.job_complete ?? null,
    unresolvedIssues: db.unresolved_issues ?? null,
    materialsUsed: db.materials_used ?? "",
    finalComments: db.final_comments ?? "",
    status: db.status ?? "Draft",
    submittedAt: db.submitted_at ?? undefined,
  };
}

// ── UI → DB (create / draft) ──────────────────────────────────────────────
// Pass the resolved job UUID as jobUuid.

export function toDbQASubmissionCreate(
  ui: QASubmission,
  jobUuid: string,
  submitted = false
): Omit<QASubmissionRow, "id" | "updated_at"> {
  const now = new Date().toISOString();

  return {
    submission_id: ui.submissionId,
    job_id: jobUuid,
    job_number: ui.jobId,
    inspector_name: ui.installerName,
    arrival_time: ui.arrivalTime || null,
    customer_present: ui.customerPresent,
    submitted_at: submitted ? (ui.submittedAt ?? now) : null,
    overall_status: submitted ? (ui.unresolvedIssues ? "Fail" : "Pass") : "Pending",
    checklist_items: ui.checklist,
    photos: ui.photos,
    notes: ui.finalComments || null,
    issues_found:
      ui.issueEncountered && ui.issueNotes
        ? [{ type: "Install Issue", description: ui.issueNotes, severity: "Medium" }]
        : [],
    issue_encountered: ui.issueEncountered,
    issue_notes: ui.issueNotes || null,
    job_complete: ui.jobComplete,
    unresolved_issues: ui.unresolvedIssues,
    materials_used: ui.materialsUsed || null,
    final_comments: ui.finalComments || null,
    status: submitted ? "Submitted" : "Draft",
  };
}

// ── UI → DB (update) ──────────────────────────────────────────────────────

export function toDbQASubmissionUpdate(
  ui: Partial<QASubmission>
): Partial<Omit<QASubmissionRow, "id" | "updated_at">> {
  const patch: Partial<Omit<QASubmissionRow, "id" | "updated_at">> = {};

  if (ui.installerName !== undefined) patch.inspector_name = ui.installerName;
  if (ui.arrivalTime !== undefined) patch.arrival_time = ui.arrivalTime || null;
  if (ui.customerPresent !== undefined) patch.customer_present = ui.customerPresent;
  if (ui.submittedAt !== undefined) patch.submitted_at = ui.submittedAt ?? null;
  if (ui.checklist !== undefined) patch.checklist_items = ui.checklist;
  if (ui.photos !== undefined) patch.photos = ui.photos;
  if (ui.issueEncountered !== undefined) patch.issue_encountered = ui.issueEncountered;
  if (ui.issueNotes !== undefined) patch.issue_notes = ui.issueNotes || null;
  if (ui.jobComplete !== undefined) patch.job_complete = ui.jobComplete;
  if (ui.unresolvedIssues !== undefined) {
    patch.unresolved_issues = ui.unresolvedIssues;
    patch.overall_status = ui.unresolvedIssues ? "Fail" : "Pass";
  }
  if (ui.materialsUsed !== undefined) patch.materials_used = ui.materialsUsed || null;
  if (ui.finalComments !== undefined) {
    patch.final_comments = ui.finalComments || null;
    patch.notes = ui.finalComments || null;
  }
  if (ui.status !== undefined) patch.status = ui.status;

  return patch;
}

// ── Batch ─────────────────────────────────────────────────────────────────

export function toUiQASubmissions(rows: QASubmissionRow[]): QASubmission[] {
  return rows.map(toUiQASubmission);
}
